import React from 'react';
import type { Quest } from '../../../../shared/NPCData';
import { TomeButton, TomeChip, TomeEntry } from '../../primitives/tome';
import { formatObjectiveProgress, questStatusAccent } from '../../../../config/ui/quest.config';
import { questEndNpcDisplay } from '../../../../shared/quests/questEndNpcDisplay';
import { NpcQuestRewards } from './NpcQuestRewards';
import { QuestGuidance } from './guidance/QuestGuidance';
import type { PlayerWhereabouts } from './guidance/playerWhereabouts';
import type { TurnInBlock } from './turnInBlock';

export type QuestCardState = 'available' | 'ready' | 'active' | 'completed';

export function questCardState(quest: Quest): QuestCardState {
  return quest.isCompleted ? 'completed' : quest.isTurnInReady ? 'ready' : quest.isAccepted ? 'active' : 'available';
}

export interface QuestCardActions {
  playerLevel: number;
  canAccept: boolean;
  canComplete: boolean;
  onQuestAccept?: (quest: Quest) => void;
  onQuestComplete?: (quest: Quest) => void;
  /** The controller hint for this quest's action, when it is the contextual one. */
  hint?: React.ReactNode;
  /** Why a ready turn-in won't fit, known before the click (bag full). */
  blocked?: TurnInBlock | null;
}

/** Objectives with progress. Canonical target names stay separate from authored directions. */
export const QuestObjectiveList: React.FC<{ quest: Quest }> = ({ quest }) => quest.objectives.length > 0
  ? <ul className="npc-quest-objectives">
    {quest.objectives.map((objective, index) => <li key={index} data-objective-ready={objective.current >= objective.required}>
      <div><strong>{objective.targetName}{objective.type === 'kill' || objective.type === 'collect' ? ` ×${objective.required}` : ''}</strong>
        <span className="npc-objective-progress">{formatObjectiveProgress(objective.current, objective.required, objective.type)}</span></div>
      {objective.description && <p>{objective.description}</p>}
    </li>)}
  </ul> : null;

/** Accept / Turn In / Completed. Eligibility stays with the caller's interaction owner. */
export const QuestActionButton: React.FC<{ quest: Quest } & QuestCardActions> = ({
  quest, playerLevel, canAccept, canComplete, onQuestAccept, onQuestComplete, hint, blocked,
}) => <div className="npc-quest-action">
  {quest.isCompleted ? <span className="npc-quest-complete">Quest Completed</span> : quest.isAccepted
    ? <>
      <TomeButton tone="primary" fullWidth disabled={!canComplete || !onQuestComplete || !!blocked}
        onClick={() => canComplete && !blocked && onQuestComplete?.(quest)}>
        <span>{blocked ? blocked.label : quest.isTurnInReady ? 'Turn In Quest' : 'Objectives Incomplete'}</span>
        {canComplete && !blocked && onQuestComplete && hint}
      </TomeButton>
      {blocked && <p className="npc-quest-blocked" data-testid={`quest-turn-in-blocked-${quest.id}`}>{blocked.detail}</p>}
    </>
    : <TomeButton tone="primary" fullWidth disabled={!canAccept || !onQuestAccept}
      onClick={() => canAccept && onQuestAccept?.(quest)}>
      <span>{playerLevel < quest.requiredLevel ? `Requires Level ${quest.requiredLevel}` : 'Accept Quest'}</span>
      {canAccept && onQuestAccept && hint}
    </TomeButton>}
</div>;

export const QuestTurnInLine: React.FC<{ quest: Quest }> = ({ quest }) => quest.isAccepted && !quest.isCompleted
  ? <p className="npc-quest-turn-in" data-testid={`quest-end-npc-${quest.id}`}>
    Turn in to: <strong>{quest.endNpcName ?? questEndNpcDisplay(quest.id).name}</strong>
  </p> : null;

interface QuestCardProps extends QuestCardActions {
  quest: Quest;
  /** Full: story and map open (offers). Compact: story and map folded away (turn-ins, work in progress). */
  variant: 'full' | 'compact';
  you?: PlayerWhereabouts | null;
}

/** One ordinary quest. Featured quests render as a codex page instead (codex/QuestCodexPage). */
export const QuestCard: React.FC<QuestCardProps> = ({ quest, variant, you, ...actions }) => {
  const state = questCardState(quest);
  // Work in progress is the most compact: its rewards wait in the fold until it is ready.
  const foldRewards = variant === 'compact' && state === 'active';
  const status = questStatusAccent(quest.isCompleted, quest.isAccepted, quest.isTurnInReady);
  const story = <>
    <p className="npc-quest-description">{quest.description}</p>
    {!quest.isCompleted && <QuestGuidance quest={quest} you={you} />}
  </>;
  return <TomeEntry className="npc-quest-card" aria-label={quest.title} tone={state}
    density={variant} data-quest-id={quest.id} data-quest-state={state} data-quest-variant={variant}
    kicker={`Level ${quest.requiredLevel}`} title={quest.title}
    status={<TomeChip className="npc-quest-status" tone={state} data-chip={state}>{status.label}</TomeChip>}>
    {variant === 'full' ? story : <>
      {/* The gist stands in for the folded story until it is opened. */}
      <p className="npc-quest-gist" aria-hidden="true">{quest.description}</p>
      <details className="npc-quest-story">
        <summary>{foldRewards ? 'Story, map & rewards' : 'Story & map'}</summary>{story}
        {foldRewards && <NpcQuestRewards quest={quest} />}
      </details>
    </>}
    <QuestTurnInLine quest={quest} />
    <QuestObjectiveList quest={quest} />
    {!foldRewards && <NpcQuestRewards quest={quest} />}
    <QuestActionButton quest={quest} {...actions} />
  </TomeEntry>;
};
